import React, { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchClientesInactivos,
  setClientesInactivosFilters,
  setClientesInactivosPagination,
  clearClientesInactivosFilters,
} from "../../../slices/biSlice";
import DataTable from "../../../components/BI/DataTable";
import FilterBar from "../../../components/BI/FilterBar";
import ExportButtons from "../../../components/BI/ExportButtons";

const baseUrl = process.env.REACT_APP_API_URL;

/**
 * Formato de moneda en colones costarricenses.
 */
function formatColones(value) {
  if (value == null) return "₡0";
  return `₡${Number(value).toLocaleString("es-CR")}`;
}

/**
 * Formato de fecha legible.
 */
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-CR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Columnas para la DataTable de clientes inactivos.
 */
const columns = [
  { key: "nombreCliente", label: "Cliente" },
  { key: "telefono", label: "Teléfono", render: (row) => row.telefono || "—" },
  { key: "email", label: "Email", render: (row) => row.email || "—" },
  {
    key: "fechaUltimaCompra",
    label: "Última Compra",
    render: (row) => formatDate(row.fechaUltimaCompra),
  },
  {
    key: "diasSinComprar",
    label: "Días sin Comprar",
    sortable: true,
  },
  { key: "totalComprasHistoricas", label: "Compras Históricas" },
  {
    key: "totalGastado",
    label: "Total Gastado",
    render: (row) => formatColones(row.totalGastado),
  },
  {
    key: "ultimoProductoComprado",
    label: "Último Producto",
    render: (row) => row.ultimoProductoComprado || "—",
  },
  {
    key: "nombreMascota",
    label: "Mascota",
    render: (row) => row.nombreMascota || "—",
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    render: (row) => {
      if (!row.whatsAppUrl) return null;
      return (
        <a
          href={row.whatsAppUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-colors"
          aria-label={`Enviar mensaje por WhatsApp a ${row.nombreCliente}`}
          title="Enviar WhatsApp"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.073-1.106l-.292-.174-3.026.793.808-2.953-.19-.302A8 8 0 1112 20z" />
          </svg>
        </a>
      );
    },
  },
];

/**
 * Configuración de filtros para la FilterBar.
 */
const staticFilterConfig = [
  {
    name: "dias",
    label: "Días sin comprar",
    type: "range",
    min: 90,
    max: 730,
  },
  {
    name: "tienda",
    label: "Tienda",
    type: "select",
    options: [
      { value: "1", label: "Heredia" },
      { value: "2", label: "Guácimo" },
      { value: "3", label: "Santa Ana" },
    ],
  },
  {
    name: "marca",
    label: "Marca",
    type: "select",
    options: [], // populated dynamically
  },
];

export function ClientesInactivosPage() {
  const dispatch = useDispatch();
  const { data, total, loading, error, filters, pagination } = useSelector(
    (state) => state.bi.clientesInactivos
  );
  const { token } = useSelector((state) => state.app.user);

  // Dynamic filter options
  const [marcaOptions, setMarcaOptions] = useState([]);

  // Fetch marca options on mount
  useEffect(() => {
    if (!token) return;
    const headers = { "Content-type": "application/json", Authorization: `Bearer ${token}` };
    fetch(`${baseUrl}/marcas`, { headers })
      .then((res) => res.ok ? res.json() : [])
      .then((data) =>
        setMarcaOptions(
          data
            .map((m) => ({ value: m.nombre, label: m.nombre }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch(() => {});
  }, [token]);

  // Build filterConfig with dynamic options
  const filterConfig = React.useMemo(() => {
    return staticFilterConfig.map((f) => {
      if (f.name === "marca") return { ...f, options: marcaOptions };
      return f;
    });
  }, [marcaOptions]);

  // Fetch data on mount and when filters/pagination change
  useEffect(() => {
    dispatch(fetchClientesInactivos());
  }, [dispatch, filters, pagination]);

  // Map Redux filters to FilterBar values format
  const filterValues = {
    diasMin: filters.diasMin || "",
    diasMax: filters.diasMax || "",
    tienda: filters.tienda || "",
    marca: filters.marca || "",
  };

  const handleFilterChange = useCallback(
    (name, value) => {
      const filterUpdate = {};
      if (name === "diasMin") {
        filterUpdate.diasMin = value ? Number(value) : 90;
      } else if (name === "diasMax") {
        filterUpdate.diasMax = value ? Number(value) : 730;
      } else {
        filterUpdate[name] = value || null;
      }
      dispatch(setClientesInactivosFilters(filterUpdate));
    },
    [dispatch]
  );

  const handleClearFilters = useCallback(() => {
    dispatch(clearClientesInactivosFilters());
  }, [dispatch]);

  const handlePageChange = useCallback(
    (skip) => {
      dispatch(setClientesInactivosPagination({ skip }));
    },
    [dispatch]
  );

  const handlePageSizeChange = useCallback(
    (limit) => {
      dispatch(setClientesInactivosPagination({ limit, skip: 0 }));
    },
    [dispatch]
  );

  // Build export URL with current filters
  const exportUrl = (() => {
    const params = new URLSearchParams();
    if (filters.diasMin) params.set("diasMin", filters.diasMin);
    if (filters.diasMax) params.set("diasMax", filters.diasMax);
    if (filters.tienda) params.set("tienda", filters.tienda);
    if (filters.marca) params.set("marca", filters.marca);
    if (token) params.set("access_token", token);
    const qs = params.toString();
    return `${baseUrl}/bi/clientes-inactivos/export/excel${qs ? `?${qs}` : ""}`;
  })();

  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshCache = useCallback(async () => {
    if (refreshing) return;

    const confirm = await Swal.fire({
      title: "Actualizar listado",
      text: "Esto recalcula todos los clientes inactivos desde las ventas. Puede tardar uno o dos minutos.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Actualizar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    setRefreshing(true);
    Swal.fire({
      title: "Actualizando…",
      text: "Recalculando clientes inactivos",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);
      const response = await fetch(`${baseUrl}/bi/jobs/clientesInactivos/run`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      await dispatch(fetchClientesInactivos());
      Swal.fire({
        icon: "success",
        title: "Listado actualizado",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text:
          err?.name === "AbortError"
            ? "Tiempo de espera agotado. Intente de nuevo."
            : err?.message || "Error desconocido",
      });
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, refreshing, token]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Clientes Inactivos</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefreshCache}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 bg-purple text-white text-sm font-medium py-2 px-4 rounded hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {refreshing ? "Actualizando…" : "Actualizar listado"}
          </button>
          <ExportButtons
            exportUrl={exportUrl}
            label="Exportar Excel"
            disabled={loading || data.length === 0}
          />
        </div>
      </div>

      <FilterBar
        filters={filterConfig}
        values={filterValues}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <p className="text-sm">{error}</p>
          <button
            onClick={() => dispatch(fetchClientesInactivos())}
            className="text-sm underline mt-1 hover:text-red-900"
          >
            Reintentar
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        emptyMessage="No se encontraron clientes inactivos con los filtros seleccionados."
        pagination={{ limit: pagination.limit, skip: pagination.skip, total }}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageOptions={[50]}
        sortKey="diasSinComprar"
        sortDirection="desc"
      />
    </div>
  );
}

export default ClientesInactivosPage;
